import { useEffect, useState } from 'react';
import { Shell } from '../../components/layout/Shell.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { monEspaceApi } from '../../api/resources';
import { dateHeureSuisse, dateSuisse } from '../../lib/dates';
import { tr, trf } from '../../lib/i18n';
import { estUnRendezVous, ongletDuSignal, sorteDuSignal, titreDuSignal } from '../../lib/signaux';
import { C } from '../../theme/palette';

const SORTES_RDV = {
  chantier: '🏗 Rendez-vous de chantier',
  client: '👤 Rendez-vous avec un client',
  reunion: '🏢 Réunion au bureau',
  autre: '📌 Rendez-vous',
};

/**
 * La fiche d'un rendez-vous reçu : quand, où, avec qui, et la réponse de
 * l'employé — « ✓ J'ai vu, je serai là » ou « Je ne peux pas ».
 *
 * @param {{signal: object, onRepond: (id: number, reponse: string, texte?: string) => Promise<object>,
 *   onFerme: () => void}} props
 */
/** Les plans que le bureau a liés à ce rendez-vous. */
function PlansDuRendezVous({ rdvId }) {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    if (!rdvId || typeof monEspaceApi.plansDuRendezVous !== 'function') return undefined;
    let vivant = true;
    Promise.resolve()
      .then(() => monEspaceApi.plansDuRendezVous(rdvId))
      .then((l) => vivant && setPlans(Array.isArray(l) ? l : []))
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [rdvId]);
  if (!plans.length) return null;
  return (
    <div style={{ marginTop: 8 }} data-plans-rdv>
      <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, marginBottom: 4 }}>
        {tr('📐 Plans pour ce rendez-vous')}
      </div>
      {plans.map((p) => (
        <a
          key={p.envoiId}
          href={monEspaceApi.adresseFichierRecu(p.envoiId)}
          target="_blank"
          rel="noreferrer"
          style={{ display: 'block', fontSize: 13.5, fontWeight: 700, padding: '4px 0' }}
        >
          {p.titre || tr('Plan')}
        </a>
      ))}
    </div>
  );
}

export function RdvSignal({ signal, onRepond, onFerme }) {
  const [rdv, setRdv] = useState(null);
  const [motif, setMotif] = useState('');
  const [absent, setAbsent] = useState(false);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (typeof monEspaceApi.convocations !== 'function') return;
    let vivant = true;
    monEspaceApi
      .convocations()
      .then((liste) => {
        if (vivant) setRdv((liste || []).find((r) => r.id === signal.referenceId) || null);
      })
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [signal.referenceId]);

  const repond = (reponse) => {
    setEnCours(true);
    setErreur('');
    onRepond(signal.id, reponse, reponse === 'ABSENT' ? motif : undefined)
      .then(() => {
        setAbsent(false);
        setEnCours(false);
      })
      .catch((e) => {
        setErreur(e?.message || tr('Envoi impossible, réessaie.'));
        setEnCours(false);
      });
  };

  const heure = rdv?.heure ? rdv.heure + (rdv.heureFin ? '–' + rdv.heureFin : '') : '';
  const lieu = rdv?.lieuNom || '';
  const adresse = rdv?.adresse || '';
  const lienCarte = adresse
    ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(adresse)
    : null;
  const ligne = (libelle, valeur) =>
    valeur ? (
      <div style={{ display: 'flex', gap: 8, fontSize: 13, padding: '3px 0' }}>
        <span style={{ color: C.muted, minWidth: 74 }}>{tr(libelle)}</span>
        <span style={{ fontWeight: 600 }}>{valeur}</span>
      </div>
    ) : null;

  return (
    <div
      role="dialog"
      aria-label={tr('Rendez-vous')}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={onFerme}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.white,
          width: '100%',
          maxWidth: 520,
          borderRadius: '16px 16px 0 0',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>
          {tr(SORTES_RDV[rdv?.typeRdv] || SORTES_RDV.autre)}
        </div>
        {rdv ? (
          <div>
            {ligne('Objet', rdv.titre)}
            {ligne('Jour', dateSuisse(rdv.dateRdv))}
            {ligne('Heure', heure)}
            {ligne('Lieu', lieu)}
            {adresse && (
              <div style={{ display: 'flex', gap: 8, fontSize: 13, padding: '3px 0' }}>
                <span style={{ color: C.muted, minWidth: 74 }}>{tr('Adresse')}</span>
                <a href={lienCarte} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>
                  {adresse}
                </a>
              </div>
            )}
            {ligne('Avec qui', rdv.avecQui)}
            {ligne('Note', rdv.noteEquipe)}
            <PlansDuRendezVous rdvId={rdv.id} />
          </div>
        ) : (
          <div style={{ fontSize: 13, color: C.muted }}>{signal.detail}</div>
        )}
        {signal.auteurNom && (
          <div style={{ fontSize: 12, color: C.muted }}>
            {trf('Envoyé par {0}', signal.auteurNom)}
          </div>
        )}
        {signal.reponse && (
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              padding: '8px 10px',
              borderRadius: 10,
              background: signal.reponse === 'PRESENT' ? '#EAF3EC' : '#FBEDEB',
              color: signal.reponse === 'PRESENT' ? '#0F5532' : '#8E2B22',
            }}
          >
            {signal.reponse === 'PRESENT'
              ? tr('✓ Tu as répondu : je serai là')
              : tr('✗ Tu as répondu : je ne peux pas')}
            {signal.reponseTexte ? ' — ' + signal.reponseTexte : ''}
          </div>
        )}
        {absent ? (
          <>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {tr('Message au bureau (facultatif)')}
              <textarea
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                maxLength={1000}
                style={{
                  padding: 10,
                  borderRadius: 10,
                  border: '1px solid ' + C.border,
                  minHeight: 60,
                  fontFamily: 'inherit',
                }}
              />
            </label>
            <Btn
              label={tr('Envoyer : je ne peux pas')}
              danger
              full
              disabled={enCours}
              onClick={() => repond('ABSENT')}
            />
            <Btn label={tr('Annuler')} ghost full onClick={() => setAbsent(false)} />
          </>
        ) : (
          signal.type !== 'RDV_ANNULE' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Btn
                label={tr('Je ne peux pas')}
                danger
                ghost
                disabled={enCours}
                onClick={() => setAbsent(true)}
              />
              <Btn
                label={tr("✓ J'ai vu, je serai là")}
                color={C.green}
                disabled={enCours}
                onClick={() => repond('PRESENT')}
              />
            </div>
          )
        )}
        {erreur && <div style={{ color: C.red, fontSize: 12 }}>{erreur}</div>}
        <Btn label={tr('Fermer')} ghost full onClick={onFerme} />
      </div>
    </div>
  );
}

/**
 * La liste des signaux d'un employé, réutilisée dans sa cloche et, pour un
 * compte du bureau rattaché à une fiche, dans « Notifications ».
 *
 * @param {{signaux: object, onOuvrir: (cible: string, ouverture?: object) => void, estMagOuv?: boolean}} props
 */
export function ListeSignaux({ signaux, onOuvrir, estMagOuv = false }) {
  const [rdvOuvert, setRdvOuvert] = useState(null);
  const liste = signaux?.liste || [];

  const ouvre = (s) => {
    signaux.marqueVu(s.id);
    if (estUnRendezVous(s) || s.type === 'RDV_ANNULE') {
      setRdvOuvert(s);
      return;
    }
    const cible = ongletDuSignal(s, { estMagOuv });
    if (cible.fichier && s.envoiId && typeof monEspaceApi.adresseFichierRecu === 'function') {
      window.open(monEspaceApi.adresseFichierRecu(s.envoiId), '_blank', 'noopener');
      return;
    }
    if (cible.onglet && onOuvrir)
      onOuvrir('ouvrier', { onglet: cible.onglet, chantierId: cible.chantierId });
  };

  if (liste.length === 0) {
    return (
      <Card style={{ padding: 16, fontSize: 13, color: C.muted, textAlign: 'center' }}>
        {tr(
          "Rien pour l'instant. Quand le bureau t'envoie un message, un rendez-vous, un plan ou un document, il arrive ici.",
        )}
      </Card>
    );
  }
  const actuel = rdvOuvert ? liste.find((s) => s.id === rdvOuvert.id) || rdvOuvert : null;
  return (
    <>
      <Card>
        {liste.map((s, i) => {
          const sorte = sorteDuSignal(s.type);
          const nonVu = !s.vuLe;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => ouvre(s)}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                width: '100%',
                textAlign: 'left',
                background: nonVu ? '#F3F8FB' : C.white,
                border: 'none',
                borderTop: i === 0 ? 'none' : '1px solid ' + C.border,
                padding: '12px 12px',
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 20, lineHeight: '24px' }}>
                {sorte.icone}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 13.5,
                    fontWeight: nonVu ? 800 : 600,
                    color: C.navy,
                  }}
                >
                  {titreDuSignal(s)}
                </span>
                {s.detail && (
                  <span
                    style={{
                      display: 'block',
                      fontSize: 12.5,
                      color: C.text,
                      marginTop: 2,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {s.detail}
                  </span>
                )}
                <span style={{ display: 'block', fontSize: 11, color: C.muted, marginTop: 3 }}>
                  {[s.auteurNom, dateHeureSuisse(s.creeLe)].filter(Boolean).join(' · ')}
                  {s.nomFichier ? ' · 📎 ' + s.nomFichier : ''}
                  {s.reponse === 'PRESENT' ? ' · ' + tr('✓ Je serai là') : ''}
                  {s.reponse === 'ABSENT' ? ' · ' + tr('✗ Je ne peux pas') : ''}
                </span>
              </span>
              {nonVu && (
                <span
                  aria-label={tr('Nouveau')}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 99,
                    background: C.red,
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
              )}
            </button>
          );
        })}
      </Card>
      {actuel && (
        <RdvSignal signal={actuel} onRepond={signaux.repond} onFerme={() => setRdvOuvert(null)} />
      )}
    </>
  );
}

/**
 * La cloche de l'employé : tout ce que le bureau lui a envoyé.
 */
export function SignauxPage({ onNav, soc, estMixte, univers, setUnivers, signaux, estMagOuv }) {
  useEffect(() => {
    signaux?.relit?.();
    // À l'arrivée seulement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const nonVus = signaux?.nonVus || 0;
  return (
    <Shell
      active="ouvrier"
      onNav={onNav}
      soc={soc}
      back
      onBack={() => onNav('ouvrier')}
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
      pageTitle={tr('🔔 Ma cloche')}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0 16px' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
        >
          <div style={{ fontSize: 12.5, color: C.muted }}>
            {nonVus > 0 ? trf('{0} nouveau(x)', nonVus) : tr('Tout est vu.')}
          </div>
          {nonVus > 0 && (
            <Btn sm ghost label={tr('Tout marquer vu')} onClick={() => signaux.marqueTous()} />
          )}
        </div>
        <ListeSignaux signaux={signaux} onOuvrir={onNav} estMagOuv={estMagOuv} />
      </div>
    </Shell>
  );
}
